import { useMutation } from '@apollo/client'
import { formatPrice } from '@commerce/product/use-price'
import { Cart } from '@commerce/types/cart'
import type { Product, ProductVariant } from '@commerce/types/product'
import cartLinesAddMutation, {
  cartAddMultipleLinesVars,
} from '@commerce/utils/mutations/cart-lines-add'
import {
  ProductPaintSelection,
  ProductSurfaceSelection,
} from '@components/product'
import { SelectedOptions } from '@components/product/helpers'
import { useUI } from '@components/ui'
import {
  PAINT_UPSELL_PRODUCTS,
  PREP_COAT_UPSELL_PRODUCTS,
  checkUpsellProductExists,
} from '@lib/cart'
import { pinterestAddToCartEvent } from '@lib/events/addToCart'
import {
  PRODUCT_CUSTOM_ATTRIBUTES_KEYS,
  findMetafieldValueByKey,
} from '@lib/helpers/general'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'

interface props {
  options: ProductVariant[]
  currencyCode: string
  selectedSibling: Product
  parentProduct: Product
  prismicContent: object
  cart: Cart | null
  selectedSurfaceTitle: string
  totalLitres: number
  setTotalLitres: (totalLitres: number) => void
}

const ProductByLitreProcess: FC<props> = ({
  options,
  currencyCode,
  selectedSibling,
  parentProduct,
  prismicContent,
  cart,
  selectedSurfaceTitle,
  totalLitres,
  setTotalLitres,
}) => {
  const {
    PARENT_PRODUCT_CUSTOM_ATTRIBUTE_KEY,
    RETAIL_PRICE_CUSTOM_ATTRIBUTE_KEY,
  } = PRODUCT_CUSTOM_ATTRIBUTES_KEYS
  const { t } = useTranslation(['common'])
  const { locale } = useRouter()

  const {
    setUpsellViewContent,
    setSidebarView,
    openSidebar,
    openCartNotification,
    setUpsellProductType,
    // closeCartNotification,
    setSelectedLiters,
    selectedSurfaceRequiresPrepCoatUpsell,
    surfaceArea,
    setSurfaceArea,
    setNumberOfCoats,
    numberOfCoats,
  } = useUI()

  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({})
  const [loading, setLoading] = useState(false)
  const [totalPrice, setTotalPrice] = useState(0)

  const name = selectedSibling?.name

  const i18nName = name?.replaceAll('Lime Paint', t('lime-paint'))

  useEffect(() => {
    let litres = 0
    let subTotals = 0
    Object.keys(selectedOptions).forEach((key) => {
      const variant = options.find((o) => o.id === key)
      const quantity = Number(selectedOptions[key])
      if (variant && variant.id) {
        const quantityWithoutUnit = variant.name?.trim()?.replace(/\D/g, '')
        const quantityIsInML = variant.name
          ?.trim()
          ?.toLowerCase()
          .includes('ml')
        const litre = quantityIsInML
          ? Number(quantityWithoutUnit) / 1000
          : quantityWithoutUnit
        if (litre) {
          litres = litres + quantity * Number(litre)
        }
        if (variant && variant.price) {
          subTotals = subTotals + quantity * Number(variant.price)
        }
      }
    })
    setTotalPrice(subTotals)
    setTotalLitres(litres)
    setSelectedLiters(litres)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions])

  // const { hits, sendEvent } = useHits()
  const [addItem] = useMutation(cartLinesAddMutation, {
    onCompleted() {
      pinterestAddToCartEvent(
        Object.keys(selectedOptions).map((key) => {
          return {
            product_id: selectedSibling?.id || '',
            product_variant:
              selectedOptions?.size ||
              selectedSibling?.variants?.[0]?.name ||
              '',
            product_variant_id:
              selectedSibling?.variants?.find(
                (variant) =>
                  variant?.name?.toLowerCase() ===
                  selectedOptions?.size?.toString().toLowerCase()
              )?.id ||
              selectedSibling?.variants?.[0]?.id ||
              '',
            product_price:
              selectedSibling?.variants?.find(
                (variant) =>
                  variant?.name?.toLowerCase() ===
                  selectedOptions?.size?.toString().toLowerCase()
              )?.price ||
              selectedSibling?.variants?.[0]?.price ||
              0,
            product_quantity: Number(selectedOptions[key]),
            currency: selectedSibling?.price?.currencyCode || 'AUD',
          }
        })
      )
      setUpsellProductType(null)
      const upsellProductHandles = PAINT_UPSELL_PRODUCTS

      const upsellBrushExists = checkUpsellProductExists(
        cart!,
        upsellProductHandles
      )
      const upsellPrepCoatExists = checkUpsellProductExists(
        cart!,
        PREP_COAT_UPSELL_PRODUCTS
      )
      const language = locale?.split('-')?.[0]

      //Check selected surface text fist, see if both brush and prep-coat upsell needed
      if (selectedSurfaceRequiresPrepCoatUpsell) {
        // Brush and prep-coat already added to cart
        if (upsellBrushExists.length > 0 && upsellPrepCoatExists.length > 0)
          openCartNotification(
            `${totalLitres}L ${language === 'en' ? `of ` : ''}${i18nName} ${t(
              'added-to-cart',
              {
                ns: 'cart',
              }
            )}`
          )
        // Display brush and prep-coat upsell products
        if (
          upsellBrushExists.length === 0 ||
          upsellPrepCoatExists.length === 0
        ) {
          const upSellProducts = []
          if (upsellBrushExists.length === 0)
            upSellProducts.push(...PAINT_UPSELL_PRODUCTS)
          if (upsellPrepCoatExists.length === 0) {
            setUpsellProductType(
              upsellBrushExists.length === 0 ? 'BRUSH' : 'PREP_COAT'
            )
            upSellProducts.push(...PREP_COAT_UPSELL_PRODUCTS)
          }
          setUpsellViewContent(
            `${totalLitres}L ${language === 'en' ? `of ` : ''}${i18nName} ${t(
              'added-to-cart',
              {
                ns: 'cart',
              }
            )}`,
            upSellProducts
          )
          setSidebarView('UPSELL_VIEW')
          openSidebar()
        }
      }
      //Only brush upsell required for the other two selected surfaces
      else {
        if (upsellBrushExists.length > 0) {
          openCartNotification(
            `${totalLitres}L ${language === 'en' ? `of ` : ''}${i18nName} ${t(
              'added-to-cart',
              {
                ns: 'cart',
              }
            )}`
          )
        } else {
          const upSellProducts = []
          upSellProducts.push(...PAINT_UPSELL_PRODUCTS)
          setUpsellViewContent(
            `${totalLitres}L ${language === 'en' ? `of ` : ''}${i18nName} ${t(
              'added-to-cart',
              {
                ns: 'cart',
              }
            )}`,
            upSellProducts
          )
          setSidebarView('UPSELL_VIEW')
          openSidebar()
        }
      }
      setLoading(false)
    },
    onError() {
      setLoading(false)
    },
  })
  const addToCart = async () => {
    setLoading(true)
    addItem({
      variables: cartAddMultipleLinesVars(
        Object.keys(selectedOptions).map((key) => {
          const variant = options.find((o) => o.id === key)
          const recipe = findMetafieldValueByKey(variant?.metafields!, 'recipe')
          const customAttributes = [
            {
              key: PARENT_PRODUCT_CUSTOM_ATTRIBUTE_KEY,
              value: parentProduct?.handle || '',
            },
            {
              key: '_selectedSurfaceRequiresPrepCoatUpsell',
              value: selectedSurfaceRequiresPrepCoatUpsell ? 'true' : 'false', // Convert boolean to string
            },
            {
              key: '_selectedSurfaceTitle',
              value: selectedSurfaceTitle || '',
            },
          ]
          if (recipe) {
            customAttributes.push({
              key: '_recipe',
              value: recipe,
            })
          }

          if (variant?.originalPrice && variant.originalPrice > variant.price) {
            customAttributes.push({
              key: RETAIL_PRICE_CUSTOM_ATTRIBUTE_KEY,
              value: formatPrice({
                amount: variant.originalPrice,
                currencyCode: currencyCode!,
                locale,
              }),
            })
          }

          return {
            merchandiseId: key,
            quantity: Number(selectedOptions[key]),
            attributes: customAttributes,
          }
        }),
        locale
      ),
      context: { locale },
    })
  }
  return (
    <div className="flex h-full flex-col gap-y-6">
      <ProductSurfaceSelection
        surfaceArea={surfaceArea}
        setSurfaceArea={setSurfaceArea}
        setNumberOfCoats={setNumberOfCoats}
        prismicContent={prismicContent}
      />
      <ProductPaintSelection
        addToCart={addToCart}
        surfaceArea={surfaceArea}
        loading={loading}
        options={options}
        currencyCode={currencyCode}
        setSelectedOptions={setSelectedOptions}
        totalLitres={totalLitres}
        numberOfCoats={numberOfCoats}
        prismicContent={prismicContent}
        totalPrice={totalPrice}
        selectedOptions={selectedOptions}
        disabledAmountSelection={selectedSurfaceTitle === ''}
        isByLitreProcess
      />
    </div>
  )
}

export default ProductByLitreProcess
